import { LinkButton } from "$components/button/link-button";
import {
  Card,
  CardBody,
  CardColors,
  CardDetails,
  CardSubtitle,
  CardSummary,
  CardTitle,
} from "$components/card";
import { Chip } from "$components/chip";
import type { HistoricAncEncounter } from "$routes/patient/[:patientId]/antenatal-care/_lib/anc-encounters";
import { Box, Grid, Typography } from "@material-ui/core";
import { format as formatDate } from "date-fns";
import { AncEncounterStatus } from "elephant-web/gql";
import type { FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLinkToExistingAntenatalCarePatient } from "./anc-encounter-context";
import { fieldFormatters } from "./constants";
import { useElephant200AdditionsFlag } from "./use-elephant-2-00-additions-flag";

const AncStatusBadge: FC<{ status: AncEncounterStatus }> = ({ status }) => {
  if (
    status === AncEncounterStatus.Completed ||
    status === AncEncounterStatus.Discharged
  ) {
    return (
      <Chip
        color="success"
        label={
          <FormattedMessage
            id="patient.anc.history.card.badge.finalised"
            defaultMessage="Finalised"
          />
        }
        data-testid="patient.anc.history.card.badge.finalised"
      />
    );
  }
  if (status === AncEncounterStatus.InProgress) {
    return (
      <Chip
        color="warning"
        label={
          <FormattedMessage
            id="patient.anc.history.card.badge.saved"
            defaultMessage="Saved — not finalised"
          />
        }
        data-testid="patient.anc.history.card.badge.saved"
      />
    );
  }
  return null;
};

type AncEncounterCardProps = {
  encounter: HistoricAncEncounter;
  readOnly?: boolean;
};

type AncStatus = Record<string, { field: string; status: string }[]>;

const AncEncouterResultField: FC<{ field: string; status: string }> = ({
  field,
  status,
}) => (
  <Grid container>
    <Grid item xs={8}>
      <Grid>
        <Typography variant="body2">{field}</Typography>
      </Grid>
    </Grid>
    <Grid item xs={4}>
      <Typography variant="body2">{status}</Typography>
    </Grid>
  </Grid>
);

const AncEncounterCard: FC<AncEncounterCardProps> = ({
  encounter,
  readOnly = false,
}) => {
  const { formatMessage } = useIntl();
  const elephant200AdditionsFlagOn = useElephant200AdditionsFlag();
  const getLinkToExistingEncounter = useLinkToExistingAntenatalCarePatient();
  const link = getLinkToExistingEncounter(encounter.id);

  const { createdBy, clinic } = encounter;

  const fields = Object.entries(encounter).reduce<AncStatus>(
    (acc, [key, value]) => {
      if (!fieldFormatters[key]) return acc;
      const { label, section, format } = fieldFormatters[key];
      acc[section] ||= [];
      acc[section].push({
        field: formatMessage(label),
        status: format(value, encounter),
      });
      return acc;
    },
    {},
  );

  const sections = Object.entries(fields);

  return (
    <Card color={CardColors.CONSULTATION}>
      <CardBody>
        <CardSummary>
          <Box display="flex" justifyContent="space-between">
            <div>
              <CardSubtitle color={CardColors.CONSULTATION}>
                <FormattedMessage
                  id="patient.anc.history.card.subtitle"
                  defaultMessage="Consultation"
                />
              </CardSubtitle>
              <CardTitle>
                <FormattedMessage
                  id="patient.anc.history.card.title"
                  defaultMessage="Visit #{num, number}: {date}"
                  values={{
                    num: encounter.visitNumber ?? 1,
                    date: formatDate(
                      new Date(encounter.createdAt),
                      "dd MMM yyyy",
                    ),
                  }}
                />
                {elephant200AdditionsFlagOn && (
                  <Box component="span" ml={1}>
                    <AncStatusBadge status={encounter.status} />
                  </Box>
                )}
              </CardTitle>
            </div>
            {!readOnly && (
              <LinkButton to={link} data-testid={"patientHistory.anc.details"}>
                <FormattedMessage
                  id="patient.anc.history.card.link"
                  defaultMessage="View details"
                />
              </LinkButton>
            )}
          </Box>
          <CardDetails>
            <Box mb={4}>
              <Grid container>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    {createdBy?.person && (
                      <FormattedMessage
                        id="patient.anc.history.card.startedBy"
                        defaultMessage="Started by: {name}"
                        values={{
                          name: (
                            <Typography
                              component="span"
                              variant="body2"
                              color="textPrimary"
                            >
                              {`${createdBy?.person?.firstname} ${createdBy?.person?.lastname}`}
                            </Typography>
                          ),
                        }}
                      />
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    {clinic && (
                      <FormattedMessage
                        id="patient.anc.history.card.clinic"
                        defaultMessage="Clinic: {name}"
                        values={{
                          name: (
                            <Typography
                              component="span"
                              variant="body2"
                              color="textPrimary"
                            >
                              {clinic?.name}
                            </Typography>
                          ),
                        }}
                      />
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Grid container>
              {sections.flatMap(([name, items]) =>
                items.map(({ field, status }) => (
                  <AncEncouterResultField
                    key={`${encounter.id}-${name}-${field}`}
                    field={field}
                    status={status}
                  />
                )),
              )}
            </Grid>
          </CardDetails>
        </CardSummary>
      </CardBody>
    </Card>
  );
};

export default AncEncounterCard;
